import { useEffect } from "react";
import { CheckCircle2, Handshake, Inbox, Loader2, Search, X } from "lucide-react";

import {
  TRUST_COMPLAINT_OPEN_STATUSES,
  TRUST_COMPLAINT_STATUS_LABELS,
  TRUST_COMPLAINT_STATUS_ORDER,
} from "@/api/trust_complaints";
import { ComplaintDetail } from "@/components/trust_suite/ComplaintDetail";
import {
  TRUST_COMPLAINT_SOURCE_OPTIONS,
  trustComplaintListMeta,
  trustComplaintListTitle,
  trustComplaintSourceLabel,
  trustComplaintStatusLabel,
  trustComplaintStatusPillClass,
  type TrustComplaintSourceFilter,
} from "@/components/trust_suite/trustSuiteDisplay";
import { SuiteTrackingLayout } from "@/components/workflow_tracking/SuiteTrackingLayout";
import { formatRelativeTime } from "@/components/module_summary/formatRelativeTime";
import { ModuleSummaryStrip } from "@/components/module_summary/ModuleSummaryStrip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useMunicipality } from "@/context/MunicipalityContext";
import {
  useTrustTrack,
  type TrustTrackQueueFilter,
  type TrustTrackStatusFilter,
} from "@/hooks/useTrustTrack";
import { cn } from "@/lib/utils";

type ComplaintQueuePanelProps = {
  focusComplaint?: {id: number; queue: TrustTrackQueueFilter} | null;
  onFocusComplaintConsumed?: () => void;
  onViewResident?: (residentId: number) => void;
  /** Fires whenever the selected concern changes, including automatic selection. */
  onSelectConcern?: (concern: {id: number; queue: TrustTrackQueueFilter}) => void;
};

const QUEUE_EMPTY_COPY: Record<TrustTrackQueueFilter, { title: string; hint: string }> = {
  open: {
    title: "No open concerns",
    hint: "Concerns from Ordinal Connect and other sources will appear here.",
  },
  closed: {
    title: "No closed concerns",
    hint: "Concerns your team closes will appear here.",
  },
  all: {
    title: "No concerns yet",
    hint: "Concerns from Ordinal Connect and other sources will appear here.",
  },
};

export function ComplaintQueuePanel({
  focusComplaint,
  onFocusComplaintConsumed,
  onViewResident,
  onSelectConcern,
}: ComplaintQueuePanelProps = {}) {
  const { municipality, municipalities, isLoading: municipalitiesLoading } = useMunicipality();
  const municipalityId = municipality?.id;

  const track = useTrustTrack({ municipalityId });
  const fileViewerEnabled = municipality?.file_viewer_access === true;

  useEffect(() => {
    if (focusComplaint == null) return;
    track.focusComplaint(focusComplaint.id, focusComplaint.queue);
    onFocusComplaintConsumed?.();
  }, [focusComplaint]);

  useEffect(() => {
    if (track.selectedId == null) return;
    onSelectConcern?.({ id: track.selectedId, queue: track.queue });
  }, [track.selectedId, track.queue]);

  const hasActiveFilters =
    track.search.trim().length > 0 ||
    track.sourceFilter !== "all" ||
    track.statusFilter !== "all";

  const clearFilters = () => {
    track.setSearch("");
    track.setSourceFilter("all");
    track.setStatusFilter("all");
  };

  const statusOptions =
    track.queue === "open" ? TRUST_COMPLAINT_OPEN_STATUSES : TRUST_COMPLAINT_STATUS_ORDER;
  const emptyCopy = QUEUE_EMPTY_COPY[track.queue];

  if (municipalitiesLoading || municipalities.length === 0 || !municipalityId) {
    return (
      <SuiteTrackingLayout
        municipalitiesLoading={municipalitiesLoading}
        municipalitiesCount={municipalities.length}
        municipalitySelected={Boolean(municipalityId)}
        emptyIcon={Handshake}
        selectMunicipalityMessage="Select a municipality above to view concerns."
        summaryStrip={null}
        sidebar={null}
        detail={null}
      />
    );
  }

  return (
    <SuiteTrackingLayout
      testId="trust-complaint-queue-panel"
      municipalitiesLoading={false}
      municipalitiesCount={municipalities.length}
      municipalitySelected
      emptyIcon={Handshake}
      selectMunicipalityMessage="Select a municipality above to view concerns."
      sidebarClassName="max-h-[60%]"
      summaryStrip={
        <ModuleSummaryStrip
          className="hidden border-b md:block"
          stats={TRUST_COMPLAINT_STATUS_ORDER.map((status) => ({
            label: TRUST_COMPLAINT_STATUS_LABELS[status],
            value: track.counts.by_status[status],
          }))}
          maxStats={4}
          loading={track.isLoading}
        />
      }
      sidebar={
        <>
          <div className="space-y-3 border-b p-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search concerns…"
                value={track.search}
                onChange={(event) => track.setSearch(event.target.value)}
                aria-label="Search concerns"
              />
            </div>

            <ToggleGroup
              type="single"
              value={track.queue}
              onValueChange={(value) => {
                if (!value) return;
                track.setQueue(value as TrustTrackQueueFilter);
                track.setSelectedId(null);
              }}
              className="grid w-full grid-cols-3"
              variant="outline"
              size="sm"
            >
              <ToggleGroupItem value="open" aria-label="Open concerns">
                Open ({track.counts.open})
              </ToggleGroupItem>
              <ToggleGroupItem value="closed" aria-label="Closed concerns">
                Closed ({track.counts.closed})
              </ToggleGroupItem>
              <ToggleGroupItem value="all" aria-label="All concerns">
                All ({track.counts.all})
              </ToggleGroupItem>
            </ToggleGroup>

            <div className="grid grid-cols-2 gap-2 lg:grid-cols-1 lg:gap-3">
              {track.queue !== "closed" ? (
                <Select
                  value={track.statusFilter}
                  onValueChange={(value) => track.setStatusFilter(value as TrustTrackStatusFilter)}
                >
                  <SelectTrigger aria-label="Filter by status">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {statusOptions.map((status) => (
                      <SelectItem key={status} value={status}>
                        {TRUST_COMPLAINT_STATUS_LABELS[status]} ({track.counts.by_status[status]})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}

              <Select
                value={track.sourceFilter}
                onValueChange={(value) => track.setSourceFilter(value as TrustComplaintSourceFilter)}
              >
                <SelectTrigger
                  aria-label="Filter by source"
                  className={cn(track.queue === "closed" && "col-span-full")}
                >
                  <SelectValue placeholder="All sources" />
                </SelectTrigger>
                <SelectContent>
                  {TRUST_COMPLAINT_SOURCE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>
                {track.isLoading
                  ? "Loading…"
                  : `${track.totalCount} concern${track.totalCount === 1 ? "" : "s"}`}
              </span>
              {hasActiveFilters ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={clearFilters}
                >
                  <X className="mr-1 h-3 w-3" />
                  Clear filters
                </Button>
              ) : null}
            </div>
          </div>

          <div className="relative min-h-0 flex-1">
            <ScrollArea className="h-full min-h-0">
              <div className="space-y-2 p-3">
                {track.isLoading && (
                  <div className="flex items-center gap-2 px-2 py-6 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading concerns…
                  </div>
                )}

                {track.error && (
                  <p className="px-2 text-sm text-destructive">Could not load concerns.</p>
                )}

                {!track.isLoading &&
                  track.complaints.map((complaint) => (
                    <button
                      key={complaint.id}
                      type="button"
                      className={cn(
                        "w-full rounded-lg border bg-background p-3 text-left transition-colors hover:bg-muted/40",
                        track.selectedId === complaint.id &&
                          "border-primary/60 bg-primary/5 ring-1 ring-primary/20",
                      )}
                      onClick={() => track.setSelectedId(complaint.id)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="line-clamp-2 min-w-0 flex-1 text-sm font-medium leading-snug">
                          {trustComplaintListTitle(complaint)}
                        </span>
                        <span className="shrink-0 text-[10px] text-muted-foreground">#{complaint.id}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span
                          className={cn(
                            "inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium",
                            trustComplaintStatusPillClass(complaint.status),
                          )}
                        >
                          {trustComplaintStatusLabel(complaint.status)}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          {trustComplaintSourceLabel(complaint.source)}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          Submitted {formatRelativeTime(complaint.submitted_at)}
                        </span>
                      </div>
                      {trustComplaintListMeta(complaint) ? (
                        <p className="mt-1 truncate text-[11px] text-muted-foreground">
                          {trustComplaintListMeta(complaint)}
                        </p>
                      ) : null}
                      {complaint.assigned_to_name ? (
                        <p className="mt-1 truncate text-[11px] text-muted-foreground">
                          Assignee: {complaint.assigned_to_name}
                        </p>
                      ) : null}
                    </button>
                  ))}

                {!track.isLoading && track.complaints.length === 0 && (
                  <div className="rounded-lg border border-dashed bg-background px-4 py-8 text-center">
                    {track.queue === "closed" ? (
                      <CheckCircle2 className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />
                    ) : (
                      <Inbox className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />
                    )}
                    <p className="text-sm font-medium">{emptyCopy.title}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {hasActiveFilters ? "Nothing matches your filters." : emptyCopy.hint}
                    </p>
                    {hasActiveFilters ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="mt-4"
                        onClick={clearFilters}
                      >
                        Clear filters
                      </Button>
                    ) : null}
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </>
      }
      detail={
        <ComplaintDetail
          municipalityId={municipalityId}
          fileViewerEnabled={fileViewerEnabled}
          complaint={track.selected}
          loading={track.isDetailLoading && !track.selected}
          updating={track.updating}
          onUpdate={track.updateComplaint}
          onViewResident={onViewResident}
        />
      }
    />
  );
}
